import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { privatePageMetadata } from "@/lib/seo";
import {
  mpAdminGetFlags,
  mpAdminListKyc,
  mpAdminListServices,
  mpAdminModerateService,
  mpAdminOverview,
  mpAdminReviewKyc,
  mpAdminSaveFlags,
} from "@/lib/marketplace/admin.functions";
import { mpAdminListDisputes, mpAdminResolveDispute } from "@/lib/marketplace/disputes.functions";


export const Route = createFileRoute("/nc/live/adm/marketplace")({
  head: () => privatePageMetadata("/nc/live/adm/marketplace", "Marketplace | BeHot.Now", "Marketplace administration."),
  component: AdminMarketplace,
});

const FLAG_DEFS = [
  { key: "marketplace_public_enabled", label: "Public access", hint: "Open the marketplace to everyone (not just admins)." },
  { key: "seller_applications_open", label: "Seller applications open", hint: "Allow new seller KYC applications." },
  { key: "orders_enabled", label: "Orders enabled", hint: "Allow buyers to place orders." },
  { key: "payouts_enabled", label: "Payouts enabled", hint: "Allow seller withdrawals of marketplace earnings." },
] as const;

function Overview() {
  const fn = useServerFn(mpAdminOverview);
  const { data } = useQuery({ queryKey: ["mp-adm-overview"], queryFn: () => fn({ data: undefined }) });
  if (!data) return null;
  const cards = [
    { label: "KYC pending", value: data.kycPending },
    { label: "Approved sellers", value: data.sellersApproved },
    { label: "Services pending", value: data.servicesPending },
    { label: "Active services", value: data.servicesActive },
    { label: "Orders", value: data.ordersTotal },
    { label: "Open disputes", value: data.openDisputes },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      {cards.map((c) => (
        <div key={c.label} className="card-surface p-4">
          <p className="text-xs text-muted-foreground">{c.label}</p>
          <p className="mt-1 font-display text-2xl font-bold">{c.value}</p>
        </div>
      ))}
    </div>
  );
}

function KycQueue() {
  const qc = useQueryClient();
  const listFn = useServerFn(mpAdminListKyc);
  const reviewFn = useServerFn(mpAdminReviewKyc);
  const [reasons, setReasons] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({ queryKey: ["mp-adm-kyc"], queryFn: () => listFn({ data: {} }) });

  const review = useMutation({
    mutationFn: (input: { applicationId: string; decision: "approve" | "reject" | "more_info"; reason?: string | undefined }) =>
      reviewFn({ data: input }),
    onSuccess: () => {
      toast.success("Decision saved");
      void qc.invalidateQueries({ queryKey: ["mp-adm-kyc"] });
      void qc.invalidateQueries({ queryKey: ["mp-adm-overview"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!data?.length) return <p className="text-sm text-muted-foreground">No applications waiting for review.</p>;

  return (
    <ul className="space-y-3">
      {data.map((a) => (
        <li key={a.id} className="card-surface p-4">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-semibold">{a.legal_name ?? "—"}</p>
            <Badge variant="outline" className="capitalize">{a.seller_type}</Badge>
            <Badge variant="secondary">{a.country ?? "?"}</Badge>
            <Badge variant="outline">{a.application_status.replaceAll("_", " ")}</Badge>
            <span className="ml-auto text-xs text-muted-foreground">
              {a.submitted_at ? new Date(a.submitted_at).toLocaleDateString() : ""}
            </span>
          </div>
          <div className="mt-2 grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
            {a.phone && <p>Phone: {a.phone}</p>}
            {a.address_line1 && (
              <p>Address: {[a.address_line1, a.address_city, a.address_postal_code, a.country].filter(Boolean).join(", ")}</p>
            )}
            {a.seller_type === "business" && (
              <>
                {a.business_name && <p>Company: {a.business_name} ({a.business_legal_form ?? "—"})</p>}
                {a.business_registration_number && <p>Reg. no: {a.business_registration_number}</p>}
                {a.trade_license_number && (
                  <p>Trade licence: {a.trade_license_number}{a.trade_license_expiry ? ` (exp. ${a.trade_license_expiry})` : ""}</p>
                )}
                {a.licensing_authority && <p>Authority: {a.licensing_authority}</p>}
                {a.incorporation_date && <p>Incorporated: {a.incorporation_date}</p>}
                {a.beneficial_owner_name && <p>Beneficial owner: {a.beneficial_owner_name}</p>}
                {a.business_address && (
                  <p>Business address: {[a.business_address, a.business_city, a.business_postal_code, a.business_country].filter(Boolean).join(", ")}</p>
                )}
                {a.business_email && <p>Business email: {a.business_email}</p>}
                {a.business_phone && <p>Business phone: {a.business_phone}</p>}
                {a.business_website && <p>Website: {a.business_website}</p>}
                {a.vat_number && <p>VAT/TRN: {a.vat_number}</p>}
                {a.tax_id && <p>Tax ID: {a.tax_id}</p>}
              </>
            )}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Input
              placeholder="Reason (for reject / more info)"
              value={reasons[a.id] ?? ""}
              onChange={(e) => setReasons((p) => ({ ...p, [a.id]: e.target.value }))}
              className="max-w-xs"
            />
            <Button size="sm" variant="hot" disabled={review.isPending}
              onClick={() => review.mutate({ applicationId: a.id, decision: "approve" })}>
              Approve
            </Button>
            <Button size="sm" variant="outline" disabled={review.isPending}
              onClick={() => review.mutate({ applicationId: a.id, decision: "more_info", reason: reasons[a.id] || undefined })}>
              Request info
            </Button>
            <Button size="sm" variant="destructive" disabled={review.isPending || !reasons[a.id]}
              onClick={() => review.mutate({ applicationId: a.id, decision: "reject", reason: reasons[a.id] })}>
              Reject
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
}

function ServicesModeration() {
  const qc = useQueryClient();
  const listFn = useServerFn(mpAdminListServices);
  const modFn = useServerFn(mpAdminModerateService);
  const [status, setStatus] = useState("pending_moderation");

  const { data, isLoading } = useQuery({
    queryKey: ["mp-adm-services", status],
    queryFn: () => listFn({ data: { status } }),
  });

  const moderate = useMutation({
    mutationFn: (input: { serviceId: string; action: "approve" | "reject" | "suspend" | "changes_required" }) =>
      modFn({ data: input }),
    onSuccess: () => {
      toast.success("Service updated");
      void qc.invalidateQueries({ queryKey: ["mp-adm-services"] });
      void qc.invalidateQueries({ queryKey: ["mp-adm-overview"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });

  return (
    <div>
      <div className="mb-4 flex gap-2">
        {["pending_moderation", "active", "changes_required", "rejected", "suspended"].map((s) => (
          <Button key={s} size="sm" variant={status === s ? "hot" : "outline"} onClick={() => setStatus(s)}>
            {s.replaceAll("_", " ")}
          </Button>
        ))}
      </div>
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !data?.length ? (
        <p className="text-sm text-muted-foreground">No services in this state.</p>
      ) : (
        <ul className="space-y-3">
          {data.map((s) => (
            <li key={s.id} className="card-surface flex flex-wrap items-center gap-3 p-4">
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold">{s.title}</p>
                <p className="text-xs text-muted-foreground">
                  ${(s.starting_price_cents / 100).toFixed(2)} · {new Date(s.created_at).toLocaleDateString()}
                </p>
              </div>
              <Badge variant="outline">{s.status.replaceAll("_", " ")}</Badge>
              {s.status === "pending_moderation" && (
                <div className="flex gap-2">
                  <Button size="sm" variant="hot" disabled={moderate.isPending}
                    onClick={() => moderate.mutate({ serviceId: s.id, action: "approve" })}>
                    Approve
                  </Button>
                  <Button size="sm" variant="outline" disabled={moderate.isPending}
                    onClick={() => moderate.mutate({ serviceId: s.id, action: "changes_required" })}>
                    Request changes
                  </Button>
                  <Button size="sm" variant="destructive" disabled={moderate.isPending}
                    onClick={() => moderate.mutate({ serviceId: s.id, action: "reject" })}>
                    Reject
                  </Button>
                </div>
              )}
              {s.status === "active" && (
                <Button size="sm" variant="destructive" disabled={moderate.isPending}
                  onClick={() => moderate.mutate({ serviceId: s.id, action: "suspend" })}>
                  Suspend
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FlagsPanel() {
  const qc = useQueryClient();
  const getFn = useServerFn(mpAdminGetFlags);
  const saveFn = useServerFn(mpAdminSaveFlags);
  const { data } = useQuery({ queryKey: ["mp-adm-flags"], queryFn: () => getFn({ data: undefined }) });

  const save = useMutation({
    mutationFn: (flags: Record<string, unknown>) => saveFn({ data: flags }),
    onSuccess: () => {
      toast.success("Flags saved");
      void qc.invalidateQueries({ queryKey: ["mp-adm-flags"] });
      void qc.invalidateQueries({ queryKey: ["mp-flags"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });

  const flags = (data ?? {}) as Record<string, boolean>;

  return (
    <div className="card-surface max-w-xl space-y-4 p-5">
      {FLAG_DEFS.map((f) => (
        <div key={f.key} className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold">{f.label}</p>
            <p className="text-xs text-muted-foreground">{f.hint}</p>
          </div>
          <Switch
            checked={flags[f.key] === true}
            onCheckedChange={(v) => save.mutate({ ...flags, [f.key]: v })}
            disabled={save.isPending}
          />
        </div>
      ))}
      <p className="text-xs text-muted-foreground">
        While "Public access" is off, only admins can see the marketplace; everyone else sees the marketing page.
      </p>
    </div>
  );
}

function DisputesQueue() {
  const list = useServerFn(mpAdminListDisputes);
  const resolveFn = useServerFn(mpAdminResolveDispute);
  const qc = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, { outcome: string; refund: string; note: string }>>({});

  const { data } = useQuery({ queryKey: ["mp-adm-disputes"], queryFn: () => list({ data: {} }) });

  const resolve = useMutation({
    mutationFn: (v: { disputeId: string; outcome: "buyer_refund" | "seller_paid" | "split"; refundUsdCents?: number; resolution: string }) =>
      resolveFn({ data: v }),
    onSuccess: () => {
      toast.success("Dispute resolved.");
      void qc.invalidateQueries({ queryKey: ["mp-adm-disputes"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Could not resolve the dispute."),
  });

  if (!data?.length) return <p className="text-sm text-muted-foreground">No disputes.</p>;

  return (
    <ul className="space-y-3">
      {data.map((d) => {
        const draft = drafts[d.id] ?? { outcome: "buyer_refund", refund: "", note: "" };
        const set = (patch: Partial<typeof draft>) => setDrafts((s) => ({ ...s, [d.id]: { ...draft, ...patch } }));
        return (
          <li key={d.id} className="card-surface p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-semibold">{d.category.replace(/_/g, " ")}</p>
              <Badge variant="secondary">{d.status}</Badge>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{d.description}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Order {d.order_id.slice(0, 8)} · total ${((d.order?.total_cents ?? 0) / 100).toFixed(2)}
            </p>
            {d.status !== "resolved" ? (
              <div className="mt-3 grid gap-2 sm:grid-cols-4">
                <select
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  value={draft.outcome}
                  onChange={(e) => set({ outcome: e.target.value })}
                >
                  <option value="buyer_refund">Refund buyer</option>
                  <option value="seller_paid">Pay seller</option>
                  <option value="split">Split</option>
                </select>
                <Input
                  placeholder="Refund USD (split)"
                  value={draft.refund}
                  onChange={(e) => set({ refund: e.target.value })}
                />
                <Input placeholder="Resolution note" value={draft.note} onChange={(e) => set({ note: e.target.value })} />
                <Button
                  variant="hot"
                  disabled={resolve.isPending || draft.note.trim().length < 5}
                  onClick={() =>
                    resolve.mutate({
                      disputeId: d.id,
                      outcome: draft.outcome as "buyer_refund" | "seller_paid" | "split",
                      ...(draft.refund ? { refundUsdCents: Math.round(Number(draft.refund) * 100) } : {}),
                      resolution: draft.note,
                    })
                  }
                >
                  Resolve
                </Button>
              </div>
            ) : (
              <p className="mt-2 rounded-md bg-muted p-2 text-sm">{d.resolution}</p>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function AdminMarketplace() {
  return (
    <div className="space-y-6">
      <Overview />
      <Tabs defaultValue="kyc">
        <TabsList>
          <TabsTrigger value="kyc">Seller KYC</TabsTrigger>
          <TabsTrigger value="services">Services</TabsTrigger>
          <TabsTrigger value="disputes">Disputes</TabsTrigger>
          <TabsTrigger value="flags">Flags</TabsTrigger>
        </TabsList>
        <TabsContent value="kyc" className="mt-4">
          <KycQueue />
        </TabsContent>
        <TabsContent value="services" className="mt-4">
          <ServicesModeration />
        </TabsContent>
        <TabsContent value="disputes" className="mt-4">
          <DisputesQueue />
        </TabsContent>
        <TabsContent value="flags" className="mt-4">
          <FlagsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

